import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Checkbox,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  Fragment,
  useCallback,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  foyerApi,
  type OfferingCompliance,
  type LicenseInput,
  type LicenseRecord,
  type LicenseTierRecord,
  type LicenseStatus,
  type FeatureSetRow,
  type ScreenCategory,
  SCREEN_CATEGORIES,
  SCREEN_CATEGORY_LABELS,
} from "../http/foyer";
import { useCan } from "../auth/AuthContext";
import { tokens } from "../theme";
import VideoCallSeatPoolPanel from "./VideoCallSeatPoolPanel";

interface Props {
  premiseId: string;
}

/**
 * The one server refusal from this card worth a real sentence rather than
 * its raw code: `already_licensed` means another row for the same product
 * is already active or pending on this premise (`DuplicateLicenseError`) -
 * reachable from Save (picking a product the picker somehow still allowed)
 * or Restore (reactivating a cancelled row onto a product with a live one
 * already). Everything else falls through to the raw message, matching
 * this card's existing precedent for `requirements_not_met`.
 */
function licenseErrorMessage(e: unknown): string {
  if ((e as { code?: string } | undefined)?.code === "already_licensed") {
    return "This product already has an active or pending licence on this premise. Edit that one instead of adding another.";
  }
  return e instanceof Error ? e.message : String(e);
}

const STATUS_OPTIONS: LicenseStatus[] = ["active", "pending", "cancelled"];

const STATUS_COLOR: Record<
  LicenseStatus,
  "success" | "warning" | "default" | "error"
> = {
  active: "success",
  pending: "warning",
  cancelled: "default",
};

/**
 * Read-only for any logged-in user; admin sees the "+ Add", edit, and
 * delete affordances. Matches the planning's Q3/a - only Nenda staff
 * (admin role) mutate licenses.
 */
/**
 * 🚨 There is no allow-list any more, and removing it was a BUG FIX.
 *
 * This table used to render only `frontlayer`, `hospitality-tv` and
 * `video-call`. Its reasoning was sound at the time — everything else was a
 * capability flag turned on by a switch elsewhere, and "showing those here
 * would invite someone to give signage a seat count that means nothing".
 *
 * Both halves of that stopped being true. The switches are gone, so a licence
 * is now the ONLY way a premise gets anything; and the dialog no longer offers
 * an amount for a product that has none, so the invitation it guarded against
 * cannot be accepted.
 *
 * What was left was a table that hid most of what it was the record of.
 * Reported by Sigge: Hasselbacken said "Basic Sweden — already licensed" in the
 * picker while the list showed no Basic Sweden at all. It held FIVE licences
 * and displayed one. A screen that silently omits rows is worse than one that
 * shows nothing: it answers the question wrongly instead of not answering.
 */

export default function PremiseLicensesCard({ premiseId }: Props) {
  // Capability, not role: licenses.ts now gates mutations on
  // `canManageLicenses` (an adminOnly key), so mirror that here.
  const isAdmin = useCan("canManageLicenses");
  const [items, setItems] = useState<LicenseRecord[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** What a withdrawal actually took, named after the fact. */
  const [notice, setNotice] = useState<string | null>(null);
  const [dialog, setDialog] = useState<
    { mode: "create" } | { mode: "edit"; record: LicenseRecord } | null
  >(null);
  /**
   * The catalogue, so a row can be read rather than decoded.
   *
   * Without it the table prints `basic-sweden` where an operator expects
   * "Basic Sweden", and a bare `0` in Quantity for a product that HAS no
   * quantity — which reads as "they bought none of it" rather than "this is
   * not counted". Losing the fetch is not fatal: the ids still identify the
   * rows, so the table degrades to what it showed before.
   */
  const [catalogue, setCatalogue] = useState<Map<string, FeatureSetRow>>(
    new Map()
  );

  /**
   * Rooms actually holding each product, keyed by featureSet.
   *
   * 🚨 Per PRODUCT, not per licence — `entitlementCompliance` sums `bought`
   * across every active licence for an offering, because two licences for one
   * product are allowed and reading a single row would answer 100 where the
   * truth is 120. That mismatch is the whole reason this column needs care:
   * see {@link roomsCellFor}.
   *
   * Best-effort. Losing it costs the column, not the table.
   */
  const [usage, setUsage] = useState<Map<string, OfferingCompliance>>(
    new Map()
  );

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await foyerApi.listFeatureSets();
        if (alive) {
          setCatalogue(new Map(res.featureSets.map((f) => [f.id, f])));
        }
      } catch {
        // Ids only. See above.
      }
      try {
        const res = await foyerApi.entitlementCompliance(premiseId);
        if (alive) {
          setUsage(new Map(res.offerings.map((o) => [o.featureSet, o])));
        }
      } catch {
        // No column. See above.
      }
    })();
    return () => {
      alive = false;
    };
  }, [premiseId]);

  /**
   * What to print in "On rooms", and when to print nothing.
   *
   * The number belongs to the PRODUCT, the row belongs to a LICENCE, and a
   * product may carry several. Printing it on every row would invite the one
   * comparison that is always wrong — this row's Quantity 10 against the
   * product's 45 rooms, when the contract is 10 + 10 and the overage is 25.
   *
   * So it is printed on the FIRST licence of each product and left blank
   * after: the fact is stated once, where it can only be read against the
   * product. The overage is judged in the banner above, which has the summed
   * figure and is the only place equipped to judge it.
   *
   * Blank is also correct for a product sold with no amount — Streaming TV is
   * not bought forty at a time — and for one nobody has assigned yet.
   */
  const roomsCellFor = (l: LicenseRecord, firstOfProduct: boolean) => {
    if (!firstOfProduct) return null;
    const row = usage.get(l.featureSet ?? l.product);
    return row ? row.assigned : null;
  };

  /** How this row should read: its product's name, or the id it carries. */
  const labelFor = (l: LicenseRecord) =>
    catalogue.get(l.featureSet ?? l.product)?.name ?? l.product;

  /**
   * What to print in Amount.
   *
   * 🚨 A STORED NUMBER ALWAYS WINS. The product's `seatUnit` says what should
   * be counted; the licence's `quantity` says what somebody actually sold. When
   * they disagree, the sale is the fact and the product is the opinion.
   *
   * Reported by Sigge: Basic Sweden stopped showing its quantity. It was
   * authored before the "what a licence counts" field existed, so it carries
   * `seatUnit: null` — and this function believed it over the 10 sitting on the
   * row. That is the allow-list bug again in miniature: a screen hiding data it
   * holds, on the authority of something that is merely out of date.
   */
  // `totalQuantity` (base + every tier), not the row's own `quantity` alone -
  // see `LicenseRecord.tiers`. For a row with no tiers the two are identical.
  const amountFor = (l: LicenseRecord): number | null | "none" => {
    // `l.totalQuantity !== undefined`, not `??`: a real "unlimited" answer IS
    // `null` and must not fall back to `quantity` - only its total ABSENCE
    // (a record shaped before this field existed) should.
    const total = l.totalQuantity !== undefined ? l.totalQuantity : l.quantity;
    if (typeof total === "number" && total > 0) return total;
    const known = catalogue.get(l.featureSet ?? l.product);
    if (known && known.seatUnit === null) return "none";
    return total;
  };

  const refresh = useCallback(async () => {
    try {
      const list = await foyerApi.listLicenses(premiseId);
      // Defensive: a mocked test fetch might return a non-array.
      setItems(Array.isArray(list) ? list : []);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [premiseId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Only the seat-bearing products are listed. The rest are switched on and
  // off under Products; their rows carry no number worth editing.
  /**
   * Current or withdrawn.
   *
   * 🚨 `LicenseStatus` has THREE values and this menu has two, so one of them
   * has to be placed deliberately. `pending` is an ORDER — something the
   * customer is getting — so it belongs with the current ones, and its own
   * status chip still says `pending` in the row.
   *
   * The alternative, a tab per status, would leave a pending licence in a
   * third place nobody looks. That is how the allow-list bug happened: rows
   * that existed, counted, and were drawn nowhere. Two tabs, and every row is
   * under exactly one of them.
   */
  const [tab, setTab] = useState<"current" | "cancelled">("current");

  /**
   * Which multi-category products (Hotel/Care + Common area + Public
   * viewing, sold as separate pools of the same product) are expanded to
   * show their per-category rows. A product with only one row - the
   * overwhelming majority, and every product before `screenCategory`
   * existed - never has anything to expand and renders exactly as before.
   */
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(
    new Set()
  );
  const toggleExpanded = (key: string) =>
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const currentCount = (items ?? []).filter(
    (l) => l.status !== "cancelled"
  ).length;
  const cancelledCount = (items ?? []).filter(
    (l) => l.status === "cancelled"
  ).length;

  const rows =
    items === null
      ? null
      : items.filter((l) =>
          tab === "cancelled"
            ? l.status === "cancelled"
            : l.status !== "cancelled"
        );
  /**
   * Platform first, then content — the shape of what a customer bought.
   *
   * Reported by Sigge: the Products page read "a bit weird" because Licenses
   * listed Streaming TV, Frontlayer and Basic-Sweden in one undifferentiated
   * run while the card below showed a different subset. The two answer
   * different questions (this is the CONTRACT; that is whether the rooms fit
   * inside it), and the fastest way to stop them looking like a broken
   * duplicate is to make this one's shape legible.
   *
   * Split on `capability`, not on a hardcoded list: a product that unlocks a
   * Nenda capability IS the platform half, and the catalogue already says so.
   * A second copy of `CAPABILITY_PRODUCTS` on the client would drift the first
   * time a product was added.
   *
   * 🚨 Everything unmatched lands in Content rather than being dropped. A
   * licence authored before the product catalogue existed carries free text and
   * no capability, and this card's own history is the argument: it once showed
   * one of five licences, and "a screen that silently omits rows is worse than
   * one that shows a row it cannot fully explain" is the rule that came out of
   * it.
   */
  const isPlatform = (l: LicenseRecord) =>
    Boolean(catalogue.get(l.featureSet ?? l.product)?.capability);
  const platformRows = (rows ?? []).filter(isPlatform);
  const contentRows = (rows ?? []).filter((l) => !isPlatform(l));
  // One heading over a list with nothing to contrast it against is noise, so
  // the headers appear only when both halves exist.
  const grouped = platformRows.length > 0 && contentRows.length > 0;

  /**
   * The first licence row of each product, so "On rooms" is printed once.
   *
   * Built over `rows` in render order, which is the order the two groups are
   * drawn in, so "first" means the first one an operator's eye reaches.
   */
  const over = [...usage.values()].filter((o) => o.over > 0);

  const seenProducts = new Map<string, string>();
  for (const l of rows ?? []) {
    const key = l.featureSet ?? l.product;
    if (!seenProducts.has(key)) seenProducts.set(key, l.id);
  }

  /**
   * What this premise already holds, for gating the product picker.
   *
   * Both id fields, because the switches wrote `product` and left `featureSet`
   * null — reading one would make a premise with Streaming TV visibly ON fail
   * to count as holding it, and the picker would refuse every content package
   * while the screen said the parent was right there.
   */
  const heldProducts = new Set(
    (items ?? [])
      .filter((l) => l.status === "active")
      .flatMap((l) => [l.featureSet, l.product])
      .filter((v): v is string => !!v)
  );
  /**
   * Which screen categories (Hotel/Care, Common area, Public viewing) each
   * product already has a ROW for — active or pending — keyed by product id.
   *
   * ⚠️ Deliberately separate from {@link heldProducts}, and the difference is
   * the point. `held` answers "is this requirement SATISFIED", where only an
   * active licence counts: a pending Frontlayer grants nothing yet, so
   * nothing may stand on it. This one answers "is there already a row for
   * THIS CATEGORY of this product", where a pending order counts too —
   * otherwise an operator adds a second Frontlayer beside the one waiting to
   * be activated, and the duplicate only appears when it goes live.
   *
   * Cancelled is absent from both. A withdrawn product is one you may sell
   * again, and blocking that would make a withdrawal permanent by accident.
   *
   * The value stored per category is `l.screenCategory ?? "null"` — the
   * string `"null"` stands for the pre-2026-09-07 "unclassified" bucket,
   * which is its own slot (see `DuplicateLicenseError`'s server-side twin,
   * `assertNoLiveDuplicate`) and never blocks any of the three real
   * categories a NEW row would pick.
   */
  const takenCategoriesByProduct = new Map<string, Set<string>>();
  for (const l of items ?? []) {
    if (l.status !== "active" && l.status !== "pending") continue;
    const key = l.screenCategory ?? "null";
    for (const id of [l.featureSet, l.product]) {
      if (!id) continue;
      const set = takenCategoriesByProduct.get(id) ?? new Set<string>();
      set.add(key);
      takenCategoriesByProduct.set(id, set);
    }
  }

  const hasVideoCalling = !!items?.some(
    (l) => l.product === "video-call" && l.status === "active"
  );

  async function onDelete(id: string) {
    /**
     * WITHDRAW, not delete.
     *
     * The product switches this table replaced took the dependants with them
     * when something went off — a video-call pool on a place with no Care is a
     * pool nobody can be added to. Removing one row and leaving the rest active
     * is a quieter version of the same mistake, so the server cascades.
     *
     * And it cancels rather than destroys: the row carries the AGREED PRICE,
     * the only record of a held rate. Withdrawing is reversible by a human
     * decision; a delete is reversible by nobody.
     *
     * What else goes is named AFTER the call, from what the server actually
     * cancelled, rather than guessed here from a catalogue this card would have
     * to fetch and keep in step. A wrong warning is worse than a late one.
     */
    if (
      !window.confirm(
        "Withdraw this product from the premise?\n\nAnything that depends on it is withdrawn too. The licences are kept, cancelled, so the agreed price is not lost."
      )
    ) {
      return;
    }
    try {
      const { cancelled } = await foyerApi.withdrawLicense(id);
      setErr(null);
      // Named only when it took MORE than the row that was clicked — a list of
      // one repeats what the operator just did.
      setNotice(
        cancelled.length > 1 ? `Withdrew ${cancelled.join(", ")}.` : null
      );
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  /**
   * One licence row - used both directly (a product with only one row, the
   * overwhelming majority) and as an indented sub-row under a multi-category
   * product's summary row. `labelOverride` is what makes the second case
   * possible without a second copy of every cell: a sub-row shows its own
   * screen category (Hotel/Care / Common area / Public viewing) instead of
   * repeating the product name, which the summary row above it already said.
   */
  function renderRow(row: LicenseRecord, labelOverride?: ReactNode) {
    return (
      <TableRow key={row.id} hover data-testid={`license-row-${row.id}`}>
        <TableCell sx={labelOverride ? { pl: 5 } : undefined}>
          {labelOverride ?? labelFor(row)}
          {!labelOverride && !row.featureSet && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
              title="Written before the product catalogue existed. Editing it will name a product."
            >
              — legacy
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={row.status}
            size="small"
            color={STATUS_COLOR[row.status]}
            variant={row.status === "cancelled" ? "outlined" : "filled"}
          />
        </TableCell>
        {tab === "cancelled" ? (
          <TableCell data-testid={`license-cancelled-by-${row.id}`}>
            {row.cancelledBy ? (
              <>
                {row.cancelledBy}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {row.cancelledAt ? fmt(row.cancelledAt) : ""}
                </Typography>
              </>
            ) : (
              /* Cancelled before the stamp existed. Saying so beats an
             empty cell, which reads as "nobody" rather than "not
             recorded" — and those are different answers. */
              <Typography variant="caption" color="text.secondary">
                not recorded
              </Typography>
            )}
          </TableCell>
        ) : (
          <TableCell>{fmt(row.createdAt)}</TableCell>
        )}
        <TableCell align="right">
          {(() => {
            const a = amountFor(row);
            if (a === "none") return "—";
            // `null` on a product that DOES count is a site licence.
            return a ?? "Unlimited";
          })()}
          {/* `?? []`: a mocked/partial record predating this
              field must not crash the whole table over a hint. */}
          {(row.tiers ?? []).length > 0 && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.5 }}
              title={`${row.quantity ?? "Unlimited"} base + ${(row.tiers ?? [])
                .map((t) => t.quantity ?? "unlimited")
                .join(" + ")}`}
              data-testid={`license-tiers-hint-${row.id}`}
            >
              ({(row.tiers ?? []).length + 1} lines)
            </Typography>
          )}
        </TableCell>
        {tab === "cancelled" ? null : (
          <TableCell align="right" data-testid={`license-onrooms-${row.id}`}>
            {(() => {
              const key = row.featureSet ?? row.product;
              const rooms = roomsCellFor(row, seenProducts.get(key) === row.id);
              return rooms ?? "—";
            })()}
          </TableCell>
        )}
        <TableCell sx={{ maxWidth: 280 }}>
          <Typography
            variant="body2"
            noWrap
            title={row.notes ?? ""}
            color="text.secondary"
          >
            {row.notes ?? ""}
          </Typography>
        </TableCell>
        {isAdmin && (
          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                aria-label="Edit license"
                onClick={() => setDialog({ mode: "edit", record: row })}
                data-testid={`license-edit-${row.id}`}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {/* Withdrawing something already withdrawn is a button
            that does nothing, and a button that does nothing is
            read as a button that is broken. Edit remains, and it
            IS the way back: setting a cancelled licence to active
            goes through `licenseStore.update`, which re-checks the
            requirements before granting anything. */}
            {row.status !== "cancelled" && (
              <Tooltip title="Withdraw">
                <IconButton
                  size="small"
                  aria-label="Withdraw license"
                  onClick={() => onDelete(row.id)}
                  data-testid={`license-delete-${row.id}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        )}
      </TableRow>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">License overview</Typography>
        {isAdmin && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setDialog({ mode: "create" })}
            data-testid="license-add"
          >
            Add license
          </Button>
        )}
      </Stack>

      {/* The overage finding, judged on the SUMMED contract.
          It cannot live in a row: a product may carry several licences, so the
          only figure worth comparing against the rooms is the total, and the
          row does not have it. Deliberately not phrased as a violation — the
          usual cause is a contract change landing before the rooms were
          re-assigned, which is ordinary and nobody's fault. Nothing is ever
          switched off: `enabled` means "stop selling", never "stop playing",
          and a mid-stay downgrade must not black out a television. */}
      {over.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="compliance-over">
          {over.length === 1
            ? `${over[0].name} is on more rooms than were bought.`
            : `${over.length} products are on more rooms than were bought.`}{" "}
          Nothing has been switched off — reduce the rooms, or sell the seats.
        </Alert>
      )}

      {/* The counts are on the tabs rather than discovered by clicking: "what
          did this customer stop paying for" is answerable at a glance, which
          is the overview this menu exists to give. */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as typeof tab)}
        sx={{ mb: 2, minHeight: 36 }}
      >
        <Tab
          value="current"
          label={`Active (${currentCount})`}
          data-testid="license-tab-current"
          sx={{ minHeight: 36, py: 0 }}
        />
        <Tab
          value="cancelled"
          label={`Cancelled (${cancelledCount})`}
          data-testid="license-tab-cancelled"
          sx={{ minHeight: 36, py: 0 }}
        />
      </Tabs>

      {err && (
        <Typography variant="caption" color="error" sx={{ display: "block" }}>
          {err}
        </Typography>
      )}

      {notice && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
          data-testid="license-withdraw-notice"
        >
          {notice}
        </Typography>
      )}

      {/* The video-call pool is one of the rows in the table below, but the
          number that matters (seats LEFT) needs the seat summary, and the
          product string has to be exact - so it gets a first-class panel.
          Hidden unless the product is on: a seat pool for something this place
          has not bought is an invitation to buy seats nobody can use. */}
      {hasVideoCalling && (
        <VideoCallSeatPoolPanel
          premiseId={premiseId}
          licenses={items}
          onChanged={refresh}
        />
      )}

      {rows === null ? (
        <Typography variant="caption" color="text.secondary">
          Loading…
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {tab === "cancelled"
            ? "Nothing has been withdrawn from this place."
            : "Nothing licensed for this place yet. "}
          {tab === "current" && isAdmin
            ? "Everything a customer gets — capabilities and channel packages alike — is added here."
            : ""}
        </Typography>
      ) : (
        <Table size="small" data-testid="license-list">
          <TableHead>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>Status</TableCell>
              {/* Only on the tab where it exists. A permanently empty column
                  is a question the table keeps asking and never answers. */}
              {tab === "cancelled" ? (
                <TableCell>Withdrawn by</TableCell>
              ) : (
                <TableCell>Created</TableCell>
              )}
              <TableCell align="right">Quantity</TableCell>
              {/* Only on the tab where it means something. Usage is computed
                  from ACTIVE licences, so on the cancelled tab every cell
                  would be blank — a question the table keeps asking and never
                  answers. */}
              {tab === "cancelled" ? null : (
                <Tooltip title="Rooms holding this product. Counted per product across all its licences, so it is shown once — and only for products sold by an amount.">
                  <TableCell align="right">On rooms</TableCell>
                </Tooltip>
              )}
              <TableCell>Notes</TableCell>
              {isAdmin && <TableCell sx={{ width: 96 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {(grouped
              ? [
                  ["Platform", platformRows] as [
                    string | null,
                    LicenseRecord[],
                  ],
                  ["Content", contentRows] as [string | null, LicenseRecord[]],
                ]
              : [[null, rows] as [string | null, LicenseRecord[]]]
            ).map(([heading, group], groupIndex) => (
              <Fragment key={heading ?? "all"}>
                {heading && group.length > 0 && (
                  /* The separation has to read as a BREAK, not a label: a
                     caption alone left the two halves looking like one list
                     with a word in it. A ruled band, a gap above it, and the
                     count together say "different kind of thing" before
                     anything is read. The rule is skipped on the first group —
                     the table head is already its top edge. */
                  <TableRow>
                    <TableCell
                      colSpan={
                        (isAdmin ? 6 : 5) + (tab === "cancelled" ? 0 : 1)
                      }
                      sx={{
                        py: 1,
                        pt: groupIndex === 0 ? 1 : 3,
                        borderBottom: `1px solid ${tokens.divider}`,
                        borderTop:
                          groupIndex === 0
                            ? "none"
                            : `1px solid ${tokens.divider}`,
                        /* 🚨 `tokens.*` are CSS custom properties, so MUI's
                           `alpha()` throws on them ("Unsupported
                           `var(--fy-ink-faint)` color") — at render time, in
                           the browser as well as in a test. `rowAlt` is the
                           theme's own tint for exactly this, already correct in
                           both light and dark. Never wrap a token in `alpha`. */
                        bgcolor: tokens.rowAlt,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: tokens.inkMuted,
                            textTransform: "uppercase",
                            letterSpacing: 1.1,
                            fontWeight: 800,
                            fontSize: 11,
                          }}
                        >
                          {heading}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: tokens.inkFaint, fontSize: 11 }}
                        >
                          {/* What each half is FOR, said once. The two boxes on
                              this page stopped looking like duplicates when the
                              lower one was named; this is the same fix one
                              level down. */}
                          {heading === "Platform"
                            ? "· what the hotel runs on"
                            : "· what its guests can watch"}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
                {(() => {
                  // Group this section's rows by product, preserving the
                  // order they first appear in - a product with only one
                  // row (the overwhelming majority) renders exactly as
                  // before; 2+ rows means several screen-category pools of
                  // the same product, and gets a summary row that expands.
                  const byProduct = new Map<string, LicenseRecord[]>();
                  for (const row of group) {
                    const key = row.featureSet ?? row.product;
                    byProduct.set(key, [...(byProduct.get(key) ?? []), row]);
                  }
                  return [...byProduct.entries()].map(
                    ([productKey, productRows]) => {
                      // Only genuinely DIFFERENT categories earn an
                      // accordion. Several rows sharing one category
                      // (always "unclassified" in practice - every row that
                      // predates `screenCategory`) is the exact shape a past
                      // bug hid ("it held FIVE licences and displayed one"),
                      // so it renders as plain flat rows, same as ever.
                      const distinctCategories = new Set(
                        productRows.map((r) => r.screenCategory ?? null)
                      );
                      if (
                        productRows.length === 1 ||
                        distinctCategories.size === 1
                      ) {
                        return (
                          <Fragment key={productKey}>
                            {productRows.map((row) => renderRow(row))}
                          </Fragment>
                        );
                      }
                      const expanded = expandedProducts.has(productKey);
                      // Any unlimited row makes the whole product unlimited;
                      // otherwise the categories' amounts simply add up.
                      const amounts = productRows.map(amountFor);
                      const summed = amounts.every((a) => a === "none")
                        ? "none"
                        : amounts.some((a) => a === null)
                          ? null
                          : amounts.reduce(
                              (sum: number, a) =>
                                sum + (typeof a === "number" ? a : 0),
                              0
                            );
                      return (
                        <Fragment key={productKey}>
                          <TableRow
                            hover
                            sx={{ cursor: "pointer" }}
                            onClick={() => toggleExpanded(productKey)}
                            data-testid={`license-group-${productKey}`}
                          >
                            <TableCell>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                              >
                                <IconButton
                                  size="small"
                                  aria-label={expanded ? "Collapse" : "Expand"}
                                  data-testid={`license-group-toggle-${productKey}`}
                                >
                                  {expanded ? (
                                    <ExpandLessIcon fontSize="small" />
                                  ) : (
                                    <ExpandMoreIcon fontSize="small" />
                                  )}
                                </IconButton>
                                <Typography variant="body2">
                                  {labelFor(productRows[0])}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  · {productRows.length} categories
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell align="right">
                              {summed === "none"
                                ? "—"
                                : (summed ?? "Unlimited")}
                            </TableCell>
                            {tab === "cancelled" ? null : <TableCell />}
                            <TableCell />
                            {isAdmin && <TableCell />}
                          </TableRow>
                          {expanded &&
                            productRows.map((row) =>
                              renderRow(
                                row,
                                row.screenCategory
                                  ? SCREEN_CATEGORY_LABELS[row.screenCategory]
                                  : "Unclassified"
                              )
                            )}
                        </Fragment>
                      );
                    }
                  );
                })()}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      )}

      {dialog && (
        <LicenseDialog
          premiseId={premiseId}
          held={heldProducts}
          takenCategories={takenCategoriesByProduct}
          /* Already in hand: this card lists every licence, cancelled ones
             included — the Cancelled tab is a filter over the same array. A
             round trip for data already on screen would be waste. */
          cancelled={(items ?? []).filter((l) => l.status === "cancelled")}
          initial={dialog.mode === "edit" ? dialog.record : null}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await refresh();
          }}
          /* Adding/removing a tier is its own mutation, already written the
             moment it happens - unlike Save, it must not close the dialog
             (an operator adding several lines in a row would be kicked out
             after the first). The card's OWN list still needs the fresh
             totals, so a background refresh runs without touching `dialog`. */
          onTiersChanged={() => void refresh()}
        />
      )}
    </Paper>
  );
}

function LicenseDialog({
  premiseId,
  held,
  takenCategories,
  cancelled,
  initial,
  onClose,
  onSaved,
  onTiersChanged,
}: {
  premiseId: string;
  /** Product ids this premise already holds an ACTIVE licence for. */
  held: Set<string>;
  /** Screen categories (or `"null"` for the legacy/unclassified bucket)
   *  already carrying a row - active or pending - per product id. */
  takenCategories: Map<string, Set<string>>;
  /** Withdrawn licences, so their agreed terms can be offered back. */
  cancelled: LicenseRecord[];
  initial: LicenseRecord | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  /** Called after a tier is added or removed, to refresh the card behind this
   *  still-open dialog - see the call site's comment. */
  onTiersChanged: () => void;
}) {
  /**
   * WHICH PRODUCT, chosen from the catalogue — not typed.
   *
   * This was a free-text box defaulting to "hospitality-tv", with a hardcoded
   * list of three names beside it. That was correct when a licence recorded a
   * CAPABILITY and nothing joined it to anything: a typo cost nothing because
   * nothing read the value.
   *
   * `License.featureSet` changed that. It is the join the compliance card reads
   * to answer "did this customer get what they paid for", and a typed string
   * cannot join to anything — "basic-sweden " with a trailing space is a
   * product nobody sells and a customer with no content.
   */
  const [featureSet, setFeatureSet] = useState<string>(
    initial?.featureSet ?? ""
  );
  const [products, setProducts] = useState<
    | {
        id: string;
        name: string;
        requires: string[];
        seatUnit: FeatureSetRow["seatUnit"];
      }[]
    | null
  >(null);
  /**
   * What this customer pays, in MAJOR units. Stored in minor ones.
   *
   * NOT an override of a product price. `FeatureSet.price` is carried verbatim
   * from the portal — whose FeatureSet WAS a priced product offering — and a
   * product here is the technical representation of what we sell. What a
   * customer pays is agreed per customer, which is also the only place a held
   * rate can live.
   */
  /**
   * What the chosen product counts, or `null` for a product with no amount.
   * Read from the catalogue rather than a list kept here, so adding a product
   * server-side does not need a second edit on this screen.
   */
  const chosen = products?.find((p) => p.id === featureSet);
  /**
   * ⚠️ Two nulls that are NOT the same, and `??` cannot tell them apart.
   *
   * `chosen?.seatUnit ?? "room"` reads fine and is wrong: a product whose
   * `seatUnit` is legitimately `null` (Signage — on or off, no amount) collapses
   * onto the fallback and gets asked for a count anyway. The fallback is for an
   * UNKNOWN product — still loading, or a legacy free-text row — where hiding
   * the field on a guess would silently drop a seat count.
   */
  const seatUnit = chosen ? chosen.seatUnit : "room";

  /**
   * Does a licence for this product carry a VOLUME — an amount and a price?
   *
   * 🚨 Two shapes of licence, and showing one form for both is what made this
   * dialog cluttered. Ruled by Sigge:
   *
   * > "If you pick a product that does not have any impact on volume
   * >  (Streaming TV - the volume license is the channels, Care - the volume
   * >  license is the frontlayer) I dont think we should clotter the UI with
   * >  price and amount"
   *
   *   VOLUME       an amount and an agreed price — 40 rooms of Basic Sweden,
   *                40 screens of Frontlayer, 10 residents of Video calling.
   *   ENABLEMENT   the premise has it or does not. The money and the count sit
   *                on the product it depends on, and asking again here invites
   *                somebody to enter a second number that nothing adds up.
   *
   * `seatUnit: null` already says exactly this, so it drives the form rather
   * than a second field that could disagree with it.
   */
  /**
   * ⚠️ ...or the licence already carries one.
   *
   * A product authored before "what a licence counts" existed reports
   * `seatUnit: null`, and hiding Amount for it would not merely hide a field:
   * `save` maps "no amount" to `quantity: 0`, so opening a licence for 10 rooms
   * and pressing Save would silently sell them zero. The product's declaration
   * is what SHOULD be counted; the row's quantity is what WAS sold, and a form
   * must never destroy the second on the authority of the first.
   */
  /**
   * The terms this premise ALREADY HAD for the chosen product.
   *
   * 🚨 The whole point of cancelling rather than deleting, finally delivered.
   * A withdrawn licence keeps its AGREED PRICE, which under "upgrade now and
   * keep your price" is the only record of a held rate. Preserving that and
   * never mentioning it is just storage: an operator re-selling the product
   * types a number from memory at today's rate card, and the customer is
   * silently repriced — the exact outcome the design was built to prevent.
   *
   * Shown at the only moment it is decidable: after choosing the product,
   * before typing anything.
   */
  const carriesVolume =
    seatUnit !== null ||
    (typeof initial?.quantity === "number" && initial.quantity > 0);

  /**
   * Which of the three screen populations this row covers — see the
   * server's `ScreenCategory`. Meaningless for an enablement (no volume, no
   * subdividing "on or off" into three pools), so it is `null` there
   * regardless of what this state holds.
   *
   * `""` is a real, reachable value, but only for a legacy row that already
   * carries `screenCategory: null` — "Unclassified" is offered as an
   * explicit menu choice for exactly that row, never for a brand-new one
   * (which defaults to "Hotel/Care", the common case, and cannot be saved
   * unclassified going forward).
   */
  const [screenCategory, setScreenCategory] = useState<ScreenCategory | "">(
    initial ? (initial.screenCategory ?? "") : "room"
  );
  const effectiveCategory: ScreenCategory | null = carriesVolume
    ? screenCategory || null
    : null;

  /**
   * A cancelled row's category is reachable without an extra click.
   *
   * Without this, "restore" would be unreachable for exactly the case it
   * matters most for: a pre-2026-09-07 cancelled row carries
   * `screenCategory: null`, a brand-new licence starts at "Hotel/Care" (the
   * one category picking a product does not also require choosing), and
   * nothing would ever land the selector back on `null` to notice the
   * match. So the category follows the PRODUCT: whenever it changes (never
   * on every render - only when the operator picks a different product),
   * default to whatever a cancelled row for that product already used.
   */
  useEffect(() => {
    if (initial || !featureSet) return;
    const match = cancelled.find(
      (l) => (l.featureSet || l.product) === featureSet
    );
    setScreenCategory(match ? (match.screenCategory ?? "") : "room");
    // Reacts to the PRODUCT changing, not to `cancelled` (stable per dialog
    // open) or `initial` (never changes after mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureSet]);

  const restorable = initial
    ? null
    : (cancelled.find(
        (l) =>
          (l.featureSet || l.product) === featureSet &&
          (l.screenCategory ?? null) === effectiveCategory
      ) ?? null);

  const [restoring, setRestoring] = useState(false);

  async function restore() {
    if (!restorable) return;
    setRestoring(true);
    setErr(null);
    try {
      // Through `update`, so the requirement check runs: restoring Video
      // calling onto a premise that has since lost Care is refused rather than
      // quietly restored onto nothing. It also clears the cancellation stamp.
      await foyerApi.updateLicense(restorable.id, { status: "active" });
      await onSaved();
    } catch (e) {
      setErr(licenseErrorMessage(e));
      setRestoring(false);
    }
  }

  /**
   * Where the count actually lives, for the line that replaces the fields.
   *
   * A dependency that carries volume is the honest answer — Care sits on
   * Frontlayer, which is sold by the screen. When nothing upstream carries it,
   * the volume is downstream instead (Streaming TV is counted by the channel
   * products it delivers), and saying so beats an empty explanation.
   */
  const volumeLivesOn = (() => {
    if (!chosen || carriesVolume) return null;
    const parent = (chosen.requires ?? [])
      .map((id) => products?.find((p) => p.id === id))
      .find((p) => p && p.seatUnit !== null);
    return parent ? parent.name : null;
  })();

  const [price, setPrice] = useState<string>(
    initial?.price?.amount != null ? String(initial.price.amount / 100) : ""
  );
  const [status, setStatus] = useState<LicenseStatus>(
    initial?.status ?? "active"
  );
  const [quantity, setQuantity] = useState<string>(
    initial?.quantity != null ? String(initial.quantity) : ""
  );
  /**
   * Unlimited is now a CHOICE, not the absence of one.
   *
   * `quantity: null` means a site licence, and the dialog used to produce one
   * by leaving the field blank. So the way to accidentally grant a hotel
   * unlimited seats was to not fill something in — and nothing downstream could
   * tell that apart from somebody meaning it, because an unbounded licence can
   * never be over its allowance (`entitlementCompliance`).
   *
   * An existing row with a null quantity opens with this ticked, so the
   * distinction survives a round trip through the editor.
   */
  const [unlimited, setUnlimited] = useState(
    initial != null && initial.quantity == null
  );
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  /**
   * Additional purchase lines on this SAME row — see `LicenseRecord.tiers`.
   * Local state, not derived from `initial` on every render: adding or
   * removing one is its own request, answered immediately by the server, and
   * `initial` is a snapshot passed in when the dialog opened that this
   * component has no way to ask the parent to replace mid-edit.
   *
   * Only meaningful once the row exists — a tier is a LINE ON a licence, so
   * there is nothing to add one to before Save has created the base row.
   */
  const [tiers, setTiers] = useState<LicenseTierRecord[]>(initial?.tiers ?? []);
  const [tierQuantity, setTierQuantity] = useState("");
  const [tierUnlimited, setTierUnlimited] = useState(false);
  const [tierPrice, setTierPrice] = useState("");
  const [tierNotes, setTierNotes] = useState("");
  const [tierBusy, setTierBusy] = useState(false);
  const [tierErr, setTierErr] = useState<string | null>(null);

  async function addTier() {
    if (!initial) return;
    setTierBusy(true);
    setTierErr(null);
    try {
      const updated = await foyerApi.addLicenseTier(initial.id, {
        quantity: tierUnlimited ? null : Number(tierQuantity),
        price: tierPrice.trim()
          ? {
              currency: "SEK",
              amount: Math.round(Number(tierPrice) * 100),
              fractionDigits: 2,
            }
          : null,
        notes: tierNotes.trim() || null,
      });
      setTiers(updated.tiers);
      setTierQuantity("");
      setTierUnlimited(false);
      setTierPrice("");
      setTierNotes("");
      onTiersChanged();
    } catch (e) {
      setTierErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTierBusy(false);
    }
  }

  async function removeTier(tierId: string) {
    if (!initial) return;
    setTierBusy(true);
    setTierErr(null);
    try {
      const updated = await foyerApi.removeLicenseTier(initial.id, tierId);
      setTiers(updated.tiers);
      onTiersChanged();
    } catch (e) {
      setTierErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTierBusy(false);
    }
  }

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await foyerApi.listFeatureSets();
        if (alive) {
          setProducts(
            res.featureSets.map((f) => ({
              id: f.id,
              name: f.name,
              requires: f.requires ?? [],
              seatUnit: f.seatUnit ?? null,
            }))
          );
        }
      } catch {
        // The dialog still works on the legacy free-text path; losing the
        // catalogue must not lock somebody out of editing seats.
        if (alive) setProducts([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /** Categories already taken for this product - the `"null"` legacy bucket excluded. */
  function realCategoriesTaken(productId: string): Set<ScreenCategory> {
    const taken = takenCategories.get(productId) ?? new Set<string>();
    return new Set(
      SCREEN_CATEGORIES.filter((c) => taken.has(c))
    ) as Set<ScreenCategory>;
  }

  /**
   * Fully blocked in the product picker: an enablement (no volume) with any
   * row at all, or a volume product with all three real categories already
   * spoken for. The `"null"`/unclassified bucket never counts toward this -
   * a legacy row sitting there does not use up one of the three named slots.
   */
  function isProductFullyTaken(p: {
    id: string;
    seatUnit: FeatureSetRow["seatUnit"];
  }): boolean {
    const taken = takenCategories.get(p.id) ?? new Set<string>();
    if (p.seatUnit === null) return taken.size > 0;
    return SCREEN_CATEGORIES.every((c) => taken.has(c));
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      /**
       * 🚨 `0`, not `null`, for a product with no amount.
       *
       * `null` means UNLIMITED and lifts this premise's screen cap
       * (`seatsForPremise`). Signage has no amount at all, which is a different
       * fact — and mapping "no amount" onto "unlimited" would hand a hotel
       * infinite TV pairings for buying a signage licence.
       */
      const q = !carriesVolume ? 0 : unlimited ? null : Number(quantity);
      // Stored in MINOR units, entered in major ones. Blank is not zero: it
      // means "the product's rate card applies", which is a different fact
      // from "this customer pays nothing".
      // 🚨 An enablement carries no price, even if one was typed before the
      // product was switched. A hidden field that still saves its value is the
      // worst of both: invisible on screen, and on the invoice.
      const agreed =
        carriesVolume && price.trim()
          ? {
              currency: "SEK",
              amount: Math.round(Number(price) * 100),
              fractionDigits: 2,
            }
          : null;
      if (initial) {
        await foyerApi.updateLicense(initial.id, {
          featureSet: featureSet || null,
          price: agreed,
          status,
          screenCategory: effectiveCategory,
          quantity: q,
          notes: notes.trim() || null,
        });
      } else {
        const payload: LicenseInput = {
          premiseId,
          featureSet: featureSet || null,
          price: agreed,
          status,
          screenCategory: effectiveCategory,
          quantity: q,
          notes: notes.trim() || null,
        };
        await foyerApi.createLicense(payload);
      }
      await onSaved();
    } catch (e) {
      setErr(licenseErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? "Edit license" : "New license"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Product"
            value={featureSet}
            onChange={(e) => setFeatureSet(e.target.value)}
            select
            size="small"
            autoFocus
            disabled={products === null}
            helperText={
              products === null
                ? "Loading the catalogue…"
                : products.length === 0
                  ? "No products in the catalogue yet — create one under Distribution → Products."
                  : initial && !initial.featureSet
                    ? `This licence still carries the old free-text value “${initial.product}”. Choosing a product replaces it.`
                    : "What this customer bought. Chosen from the catalogue, so it can be joined to what their rooms actually hold."
            }
            inputProps={{ "data-testid": "license-product" }}
          >
            {(products ?? []).map((p) => {
              const missing = (p.requires ?? []).filter((r) => !held.has(r));
              const nameOf = (id: string) =>
                products?.find((x) => x.id === id)?.name ?? id;
              /**
               * FULLY sold to this premise, so not offerable again - an
               * enablement with any row, or a volume product with all three
               * screen categories already spoken for (see
               * `isProductFullyTaken`). A volume product with one or two
               * categories still open stays selectable; the category picker
               * below is what actually enforces the remaining slots.
               *
               * 🚨 The exception is the whole reason this is not just
               * `isProductFullyTaken(p)`: when EDITING a licence, its own
               * product is by definition already licensed — by the row being
               * edited. Blocking it would make the select impossible to leave
               * alone, and an operator changing only the price would find the
               * product field refusing its own value.
               */
              const isOwnProduct =
                !!initial && (initial.featureSet || initial.product) === p.id;
              const duplicate = !isOwnProduct && isProductFullyTaken(p);
              return (
                <MenuItem
                  key={p.id}
                  value={p.id}
                  // Shown and disabled rather than hidden. "Where did Video
                  // calling go?" is a support ticket; "Video calling — needs
                  // Care" is an instruction, and it is the same sentence the
                  // API would answer with.
                  disabled={duplicate || missing.length > 0}
                >
                  {p.name}
                  {duplicate && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      — already licensed
                    </Typography>
                  )}
                  {!duplicate && missing.length > 0 && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      — needs {missing.map(nameOf).join(" + ")}
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </TextField>

          {/* The top selector: which of a volume product's up to three
              pools this row is. An enablement (no volume) never subdivides -
              it is on or off for the whole premise, so there is nothing to
              pick. */}
          {carriesVolume && (
            <TextField
              label="Screen category"
              value={screenCategory}
              onChange={(e) =>
                setScreenCategory(e.target.value as ScreenCategory | "")
              }
              select
              size="small"
              helperText="Which population of screens this covers - the connector to what Rooms will assign against it."
              inputProps={{ "data-testid": "license-screen-category" }}
            >
              {SCREEN_CATEGORIES.map((c) => {
                const isOwnCategory =
                  !!initial && (initial.screenCategory ?? null) === c;
                const categoryTaken =
                  !isOwnCategory && realCategoriesTaken(featureSet).has(c);
                return (
                  <MenuItem key={c} value={c} disabled={categoryTaken}>
                    {SCREEN_CATEGORY_LABELS[c]}
                    {categoryTaken && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        — already licensed
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
              {/* Reachable two ways: editing a legacy row that already sits
                  here, or landing here automatically because a CANCELLED row
                  for the chosen product used it (see the effect above) - the
                  only way "restore" can ever be offered for one. Never a
                  starting point picked by hand for a brand-new licence, and
                  never offered again once an operator moves it to a real
                  category. */}
              {screenCategory === "" && (
                <MenuItem value="">
                  <em>Unclassified (legacy)</em>
                </MenuItem>
              )}
            </TextField>
          )}

          {restorable && (
            <Alert
              severity="info"
              data-testid="license-restorable"
              action={
                <Button
                  size="small"
                  variant="contained"
                  disabled={restoring}
                  onClick={() => void restore()}
                  data-testid="license-restore"
                >
                  Restore
                </Button>
              }
            >
              <strong>This was licensed here before.</strong>{" "}
              {restorable.quantity === null
                ? "Unlimited"
                : `${restorable.quantity}`}
              {restorable.price?.amount != null
                ? ` at ${(restorable.price.amount / 100).toLocaleString("sv-SE")} ${
                    restorable.price.currency ?? "SEK"
                  }`
                : ""}
              {restorable.cancelledBy
                ? `, withdrawn by ${restorable.cancelledBy}`
                : ""}
              . Restoring keeps those terms; continuing below sets new ones.
            </Alert>
          )}

          {carriesVolume ? (
            <TextField
              label="Price"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              size="small"
              placeholder="SEK"
              helperText="What this customer pays."
              inputProps={{
                "data-testid": "license-price",
                inputMode: "decimal",
              }}
            />
          ) : (
            /* Replaces Amount and Price rather than leaving two dead fields.
               An empty box invites a number; a sentence explains why there
               isn't one. */
            <Alert severity="info" data-testid="license-enablement">
              This premise either has {chosen?.name ?? "this"} or it does not —
              there is no amount and no separate price.
              {volumeLivesOn
                ? ` The volume is on ${volumeLivesOn}, which it needs anyway.`
                : " It is counted by the products it delivers."}
            </Alert>
          )}
          <TextField
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as LicenseStatus)}
            select
            size="small"
            inputProps={{ "data-testid": "license-status" }}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          {/* 🚨 The Amount block is present only when the product HAS an
              amount. Signage is on or off — never "12 of signage" — and asking
              anyway produces a number that means nothing, which somebody then
              has to interpret. `seatUnit: null` is NOT `quantity: null`: the
              latter means unlimited and lifts this premise's screen cap. */}
          {carriesVolume && (
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <TextField
                label="Amount"
                type="number"
                value={unlimited ? "" : quantity}
                onChange={(e) => setQuantity(e.target.value)}
                size="small"
                disabled={unlimited}
                required={!unlimited}
                inputProps={{
                  min: 1,
                  step: 1,
                  "data-testid": "license-quantity",
                }}
                helperText={
                  unlimited
                    ? "A site licence — every room, no count."
                    : seatUnit === "screen"
                      ? "How many screens this covers. Counts toward the pairing cap."
                      : seatUnit === "resident"
                        ? "How many residents this covers. Not screens — it never touches the pairing cap."
                        : "How many rooms this covers."
                }
                sx={{ flex: 1 }}
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    size="small"
                    checked={unlimited}
                    onChange={(e) => setUnlimited(e.target.checked)}
                    inputProps={
                      { "data-testid": "license-unlimited" } as Record<
                        string,
                        string
                      >
                    }
                  />
                }
                label="Unlimited"
              />
            </Stack>
          )}
          {/* Additional purchase LINES on this same row — see
              `LicenseRecord.tiers`. Only once the row exists (there is
              nothing to add a line to before Save creates the base one) and
              only for a product that carries an amount at all. */}
          {initial && carriesVolume && (
            <Stack spacing={1} data-testid="license-tiers">
              {tiers.length > 0 && (
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Additional lines
                  </Typography>
                  {tiers.map((t) => (
                    <Stack
                      key={t.id}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      data-testid={`license-tier-${t.id}`}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {t.quantity === null ? "Unlimited" : t.quantity}
                        {t.price?.amount != null
                          ? ` at ${(t.price.amount / 100).toLocaleString("sv-SE")} ${
                              t.price.currency ?? "SEK"
                            }`
                          : ""}
                        {t.notes ? ` — ${t.notes}` : ""}
                      </Typography>
                      <Tooltip title="Remove this line">
                        <IconButton
                          size="small"
                          aria-label="Remove line"
                          disabled={tierBusy}
                          onClick={() => void removeTier(t.id)}
                          data-testid={`license-tier-remove-${t.id}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              )}
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="Add line: amount"
                  type="number"
                  size="small"
                  value={tierUnlimited ? "" : tierQuantity}
                  onChange={(e) => setTierQuantity(e.target.value)}
                  disabled={tierUnlimited}
                  inputProps={{
                    min: 1,
                    step: 1,
                    "data-testid": "license-tier-quantity",
                  }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Price"
                  size="small"
                  value={tierPrice}
                  onChange={(e) => setTierPrice(e.target.value)}
                  placeholder="SEK"
                  disabled={tierUnlimited}
                  inputProps={{
                    inputMode: "decimal",
                    "data-testid": "license-tier-price",
                  }}
                  sx={{ flex: 1 }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    tierBusy ||
                    (!tierUnlimited &&
                      (!tierQuantity.trim() || Number(tierQuantity) < 1))
                  }
                  onClick={() => void addTier()}
                  data-testid="license-tier-add"
                  sx={{ mt: 0.5 }}
                >
                  Add line
                </Button>
              </Stack>
              {tierErr && (
                <Typography variant="caption" color="error">
                  {tierErr}
                </Typography>
              )}
            </Stack>
          )}
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            size="small"
            multiline
            minRows={2}
          />
          {err && (
            <Typography variant="caption" color="error">
              {err}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          /* Two refusals now. A licence naming no product joins to nothing;
             and a seat count of nothing is not the same as a site licence,
             which is why "unlimited" has to be said rather than left blank. */
          disabled={
            busy ||
            !featureSet ||
            // A product with no amount has no Amount field, so demanding one
            // would disable Save with nothing on screen to fix.
            (carriesVolume &&
              !unlimited &&
              (!quantity.trim() || Number(quantity) < 1))
          }
          data-testid="license-save"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function fmt(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "-";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Silence the unused-import warnings when MUI tree-shakes.
void Box;
